import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import API_BASE_URL from '../Config';
import { 
    Eye, Check, X, Download, AlertTriangle, Shield, TrendingUp, History, User, Wallet, FileText,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';

const BandejaMasivaAprobaciones = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPage, setGoToPage] = useState('1');

    // Modal State
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [cuentasSocio, setCuentasSocio] = useState([]);
    const [loadingDetalle, setLoadingDetalle] = useState(false);

    // Mock Logic
    const getRiskLevel = (score) => {
        if (score > 80) return { label: 'AAA - VIP', color: 'success', barColor: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50', percent: 90 };
        if (score > 50) return { label: 'B - Regular', color: 'warning', barColor: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50', percent: 50 };
        return { label: 'C - Riesgos', color: 'danger', barColor: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50', percent: 30 };
    };

    useEffect(() => {
        fetchSolicitudes();
    }, []);

    const fetchSolicitudes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/solicitudes/pendientes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error fetching requests');
            const data = await response.json();
            setSolicitudes(data);
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudieron cargar las solicitudes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchDetallesSocio = async (socioId) => {
        setLoadingDetalle(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/cuentas/socio/${socioId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCuentasSocio(data);
            }
        } catch (error) {
            console.error("Error fetching accounts", error);
        } finally {
            setLoadingDetalle(false);
        }
    };

    const handleVerDetalle = (solicitud) => {
        setSelectedSolicitud(solicitud);
        fetchDetallesSocio(solicitud.socio.id);
    };

    const handleAprobar = async () => {
        if (!selectedSolicitud) return;

        const result = await Swal.fire({
            title: '¿Confirmar Aprobación?',
            html: `Se aprobará el crédito de <b>${formatCurrency(selectedSolicitud.monto)}</b> para ${selectedSolicitud.socio.nombres} ${selectedSolicitud.socio.apellidos}.<br/><small class="text-gray-500">El desembolso se realizará en una etapa posterior.</small>`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Sí, Aprobar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/solicitudes/${selectedSolicitud.id}/aprobar`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Error al aprobar');

                await Swal.fire({
                    title: '¡Aprobado!',
                    text: 'El crédito ha sido aprobado correctamente.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                setSelectedSolicitud(null);
                fetchSolicitudes();
            } catch (error) {
                Swal.fire('Error', 'Hubo un problema procesando la aprobación.', 'error');
            }
        }
    };

    const handleRechazar = () => {
        Swal.fire({
            title: 'Motivo del Rechazo',
            input: 'textarea',
            inputLabel: 'Ingrese la razón del rechazo',
            inputPlaceholder: 'Escriba aquí...',
            showCancelButton: true,
            confirmButtonText: 'Rechazar Solicitud',
            confirmButtonColor: '#ef4444',
            inputValidator: (value) => {
                if (!value) return 'Debe ingresar un motivo';
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Determine API endpoint for rejection if exists, or mock
                Swal.fire('Rechazado', 'La solicitud ha sido rechazada.', 'info');
                setSelectedSolicitud(null);
                // fetchSolicitudes(); // Refresh if endpoint existed
            }
        });
    };

    // Helpers
    const formatCurrency = (val) => new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(val);
    const getSaldoCuenta = (codigo) => {
        const cta = cuentasSocio.find(c => c.tipoCuenta.codigo === codigo);
        return cta ? cta.saldoActual : 0;
    };

    // Filter & Pagination
    const filteredSolicitudes = solicitudes.filter(sol =>
        (sol.socio?.nombres + ' ' + sol.socio?.apellidos).toLowerCase().includes(searchTerm.toLowerCase()) ||
        sol.socio?.numeroDocumento.includes(searchTerm)
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSolicitudes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSolicitudes.length / itemsPerPage);
    const totalItems = filteredSolicitudes.length;
    const startIndex = indexOfFirstItem;
    const endIndex = Math.min(indexOfLastItem, totalItems);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    return (
        <div className="space-y-5 font-sans">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-end mb-6 gap-4">
                <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-1">Bandeja de Créditos Pendientes</h3>
                    <p className="text-gray-500 text-sm">Visualizando {filteredSolicitudes.length} solicitudes pendientes</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar socio..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <svg className="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-200 bg-white">
                                <th className="px-4 py-3 text-sm font-bold text-slate-700">Fecha / Solicitud</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700">Socio / Categoría</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700">Vendedor</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700 text-right">Monto</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700">Plan</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700">Riesgo</th>
                                <th className="px-4 py-3 text-sm font-bold text-slate-700 text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="6" className="p-8 text-center text-gray-500">Cargando...</td></tr>
                            ) : currentItems.length === 0 ? (
                                <tr><td colSpan="6" className="p-8 text-center text-gray-500">No hay solicitudes pendientes</td></tr>
                            ) : (
                                currentItems.map((sol) => {
                                    const risk = getRiskLevel((sol.id * 17) % 100);
                                    return (
                                        <tr key={sol.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-bold text-gray-800">{new Date(sol.fechaSolicitud).toLocaleDateString()}</div>
                                                <div className="text-xs text-gray-400 font-mono">SOL-{sol.id}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-blue-600">{sol.socio?.nombres} {sol.socio?.apellidos}</div>
                                                <div className="text-xs text-gray-500">{sol.socio?.numeroDocumento}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm font-bold text-slate-700">{sol.usuarioCarga ? `${sol.usuarioCarga.nombre} ${sol.usuarioCarga.apellido || ''}` : 'SISTEMA'}</div>
                                                {sol.usuarioCarga && <div className="text-[10px] text-slate-400 uppercase tracking-tighter">@{sol.usuarioCarga.nombreUsuario}</div>}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="font-bold text-gray-900">{formatCurrency(sol.monto)}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-sm text-gray-700">{sol.plazo} meses</div>
                                                <div className="text-xs text-gray-400">Cuota: ~{formatCurrency(sol.monto / sol.plazo * 1.1)}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold border ${risk.bgColor} ${risk.textColor} border-${risk.color}-200`}>
                                                    {risk.label}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center">
                                                <button
                                                    onClick={() => handleVerDetalle(sol)}
                                                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                                    title="Ver Detalle y Evaluar"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Simplified Pagination for brevity */}
                {/* Paginación estilo Premium */}
                <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Ir a</span>
                            <input 
                                type="text" 
                                className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                value={goToPage}
                                onChange={(e) => setGoToPage(e.target.value)}
                                onKeyDown={handleGoToPage}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Ver</span>
                            <select 
                                className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {[5, 10, 20, 50, 100].map(val => (
                                    <option key={val} value={val}>{val}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button 
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        <div className="flex items-center mx-2 gap-1">
                            {[...Array(totalPages)].map((_, i) => {
                                const p = i + 1;
                                if (totalPages > 5) {
                                    if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                        if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                        return null;
                                    }
                                }
                                return (
                                    <button
                                        key={p}
                                        onClick={() => handlePageChange(p)}
                                        className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>

                        <button 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
                    </div>
                </div>
            </div>

            {/* --- DETAILED EVALUATION MODAL --- */}
            {selectedSolicitud && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">

                        {/* Header */}
                        <div className="bg-white border-b border-gray-100 p-6 flex justify-between items-start">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
                                    {selectedSolicitud.socio?.nombres.charAt(0)}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Evaluación de Crédito</h2>
                                    <p className="text-gray-500">Solicitante: {selectedSolicitud.socio?.nombres} {selectedSolicitud.socio?.apellidos}</p>
                                </div>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800 border border-yellow-200`}>
                                    Pendiente
                                </span>
                                <span className="text-xs text-gray-400 mt-1">ID: {selectedSolicitud.id}</span>
                            </div>
                        </div>

                        {/* Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                                {/* Left Column: Key Stats */}
                                <div className="space-y-6">
                                    {/* Credit Request Card */}
                                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                            <FileText className="w-4 h-4" /> Solicitud
                                        </h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="text-xs text-gray-500">Monto Solicitado</label>
                                                <div className="text-2xl font-bold text-gray-900">{formatCurrency(selectedSolicitud.monto)}</div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs text-gray-500">Plazo</label>
                                                    <div className="font-medium text-gray-800">{selectedSolicitud.plazo} meses</div>
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500">Tasa</label>
                                                    <div className="font-medium text-gray-800">{selectedSolicitud.tasaAplicada}% Anual</div>
                                                </div>
                                            </div>
                                            <div className="pt-4 border-t border-dashed">
                                                <label className="text-xs text-gray-500">Cuota Estimada</label>
                                                <div className="text-lg font-bold text-blue-600">{formatCurrency(selectedSolicitud.monto / selectedSolicitud.plazo * 1.12)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Risk Badge Card */}
                                    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4" /> Riesgo Computado
                                        </h4>
                                        {(() => {
                                            const risk = getRiskLevel((selectedSolicitud.id * 17) % 100);
                                            return (
                                                <div className="text-center py-4">
                                                    <div className={`inline-block p-4 rounded-full ${risk.bgColor} mb-3`}>
                                                        <Shield className={`w-8 h-8 ${risk.textColor}`} />
                                                    </div>
                                                    <div className={`text-xl font-bold ${risk.textColor}`}>{risk.label}</div>
                                                    <p className="text-xs text-gray-400 mt-2">Basado en historial interno y categoría.</p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Middle Column: Financial Analysis */}
                                <div className="lg:col-span-2 space-y-6">
                                    {/* Guarantee Coverage */}
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                        <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                            <Shield className="w-5 h-5 text-indigo-500" /> Cobertura de Garantía
                                        </h4>

                                        {loadingDetalle ? (
                                            <div className="animate-pulse h-20 bg-gray-100 rounded"></div>
                                        ) : (
                                            <div className="space-y-6">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-500">Saldo Aportes (Garantía)</span>
                                                    <span className="font-bold text-green-600">{formatCurrency(getSaldoCuenta('APO'))}</span>
                                                </div>

                                                {/* Visual Bar */}
                                                <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
                                                    {(() => {
                                                        const aportes = getSaldoCuenta('APO');
                                                        const monto = selectedSolicitud.monto;
                                                        const percent = Math.min((aportes / (monto || 1)) * 100, 100);
                                                        const color = percent >= 100 ? 'bg-green-500' : (percent > 50 ? 'bg-yellow-500' : 'bg-red-500');
                                                        return (
                                                            <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${percent}%` }}></div>
                                                        );
                                                    })()}
                                                </div>
                                                <p className="text-xs text-gray-400">
                                                    El saldo de aportes cubre el {Math.round((getSaldoCuenta('APO') / selectedSolicitud.monto) * 100)}% del monto solicitado.
                                                    {getSaldoCuenta('APO') < selectedSolicitud.monto && <span className="text-red-500 font-bold ml-1"> (Cobertura Parcial)</span>}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Capacity / DTI */}
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                        <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                            <TrendingUp className="w-5 h-5 text-blue-500" /> Capacidad de Pago
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-xs text-gray-500 mb-1">Ingreso Estimado</div>
                                                <div className="font-bold text-gray-900">Gs. 5.500.000</div>
                                                <div className="text-[10px] text-gray-400">Declarado</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-xs text-gray-500 mb-1">Cuota Nueva</div>
                                                <div className="font-bold text-blue-600">{formatCurrency(selectedSolicitud.monto / selectedSolicitud.plazo * 1.12)}</div>
                                            </div>
                                            <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-xl">
                                                <div className="text-xs text-yellow-700 mb-1">Relación Cuota/Ingreso</div>
                                                <div className="font-bold text-yellow-800">22%</div>
                                                <div className="text-[10px] text-yellow-600">Saludable (&lt;30%)</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Active Products History */}
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                            <History className="w-5 h-5 text-purple-500" /> Historial Reciente
                                        </h4>
                                        <div className="flex gap-4">
                                            <div className="flex-1 border rounded-xl p-3 flex items-center gap-3">
                                                <div className="bg-green-100 p-2 rounded-lg text-green-600"><Check className="w-4 h-4" /></div>
                                                <div>
                                                    <div className="font-bold text-gray-800">3 Créditos</div>
                                                    <div className="text-xs text-gray-500">Pagados exitosamente</div>
                                                </div>
                                            </div>
                                            <div className="flex-1 border rounded-xl p-3 flex items-center gap-3">
                                                <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Wallet className="w-4 h-4" /></div>
                                                <div>
                                                    <div className="font-bold text-gray-800">1 Activo</div>
                                                    <div className="text-xs text-gray-500">Saldo: Gs. 1.2M</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Footer: Actions */}
                        <div className="bg-white border-t border-gray-200 p-4 flex justify-between items-center">
                            <button
                                onClick={() => setSelectedSolicitud(null)}
                                className="px-6 py-3 rounded-xl text-gray-500 font-bold hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleRechazar}
                                    className="px-6 py-3 rounded-xl bg-red-50 text-red-600 font-bold border border-red-100 hover:bg-red-100 transition-colors flex items-center gap-2"
                                >
                                    <X className="w-5 h-5" /> Rechazar
                                </button>
                                <button
                                    onClick={handleAprobar}
                                    className="px-8 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold shadow-lg shadow-green-200 hover:shadow-xl hover:scale-[1.02] transition-all flex items-center gap-2"
                                >
                                    <Check className="w-5 h-5" /> Aprobar Crédito
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BandejaMasivaAprobaciones;
